import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { HubAgencyService } from "../../shared/services/hub-agency.service";
import { NotificationService } from "../..//shared/services/notification.service";
import { Agent } from "../../shared/types/agent";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LocalStorageService } from "../../shared/services/local-storage.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Agency } from "../../shared/types/agency";
import { ErrorAlert } from "../../shared/models/error-alert";
import { HelpersService } from "../../shared/services/helpers.service";
import { TranslateService } from "@ngx-translate/core";
import {RESPONSE_OK} from "../../shared/types/system";

@Component({
  selector: "app-agent-list",
  templateUrl: "./agent-list.component.html",
  styleUrls: ["./agent-list.component.scss"],
})
export class AgentListComponent implements OnInit, OnDestroy {
  @Input() agents: Agent[] = [];
  @Input() copiedAgents: Agent[] = [];
  @Input() isAddNewAgentAvailable = true;
  @Input() loaded = false;
  @Input() agency: Agency;
  @Output() emitUpdateAgents = new EventEmitter();
  @ViewChild("removeAgentInfoModal", { static: true })
  removeAgentInfoModal: ElementRef;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  selectedAgent: Agent;
  agentsError: ErrorAlert = new ErrorAlert();
  errors = {
    email: "",
    role: "",
    error: { summary: "" },
  };
  model: any = {
    email: "",
    role: "",
  };

  agentsRoles = ["Issuer", "Manager", "Booker", "Consultant"];
  agentHasChanges = {};

  // pagination
  page = 1;
  pageSize = 15;

  constructor(
    public agencyService: HubAgencyService,
    public modalService: NgbModal,
    private _notificationSvc: NotificationService,
    public ls: LocalStorageService,
    private route: ActivatedRoute,
    private router: Router,
    private helpers: HelpersService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.parseUrl();
  }

  openModal(content) {
    this.modalService.open(content).result.then(
      (result) => { },
      (reason) => { },
    );
  }

  onStatusChange(value: boolean, agent: Agent) {
    agent.status = !!value ? "active" : "inactive";
  }

  onChange(index: number) {
    this.agentHasChanges[index] = true;
  }

  addAgent(c) {
    let body = { ...this.model };
    this.agencyService
      .addAgent(body)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (response: any) => {
          let res = response.body;
          if (res.status === RESPONSE_OK) {
            this.successSave("AGENT_LIST.NOTIFICATIONS.AGENT_ADDED", {
              email: this.model.email,
            });
            c("ok");
            this.emitUpdateAgents.emit();
          } else {
            this.errors = res;
          }
        },
        (error) => (this.agentsError = this.helpers.getError(error)),
      );
  }

  onRemoveAgentClick(agent: Agent) {
    if (this.ls.isRemoveAgentInfoModalSkipped) {
      this.removeAgent(agent);
    } else {
      this.selectedAgent = agent;
      this.openModal(this.removeAgentInfoModal);
    }
  }

  removeAgent(agent: Agent) {
    let body: any = {
      id: agent.id,
    };
    if (this.agency) {
      body.agency_id = this.agency.id;
    }

    this.agencyService
      .remove(body)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (response: any) => {
          this.successSave("AGENT_LIST.NOTIFICATIONS.AGENT_REMOVED");
          this.emitUpdateAgents.emit();
        },
        (err) =>
          this._notificationSvc.error(
            err.error?.status,
            err.error?.error,
            err.status,
          ),
      );
  }

  saveChanges(agent, idx: number) {
    let body: any = {
      id: agent.id,
      agent_id: agent.agent_id,
      role: agent.role,
      status: agent.status,
    };
    if (this.agency) {
      body.agency_id = this.agency.id;
    }

    this.agencyService
      .update(body)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (response: any) => {
          let res = response.body;
          if (res.status === RESPONSE_OK) {
            const initialAgent = this.copiedAgents.find(
              (initialAgent) => initialAgent.id === agent.id,
            );
            if (initialAgent) {
              Object.assign(initialAgent, agent);
            }
            this.agentHasChanges[idx] = false;
            this.successSave("AGENT_LIST.NOTIFICATIONS.AGENT_UPDATED");
          } else {
            this.errors = res;
            if (this.errors?.error?.summary) {
              this._notificationSvc.error(
                this.translate.instant("NOTIFICATIONS.ERROR"),
                this.errors.error.summary,
              );
            }
          }
        },
        (err) => {
          this._notificationSvc.error(
            err.error?.status,
            err.error?.error,
            err.status,
          );
        },
      );
  }

  handleInvitation(action: "resend" | "revoke", agent: Agent) {
    const body = { id: agent.id };
    const actionUrl =
      action === "resend" ? "resend-invitation" : "revoke-invitation";

    this.agencyService
      .sendInvitation(actionUrl, body)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (response: any) => {
          const res = response.body;
          if (res.status === RESPONSE_OK) {
            this.successSave(res.message);
          }
        },
        (err) => {
          this._notificationSvc.error(
            err.error?.status,
            err.error?.error,
            err.status,
          );
        },
      );
  }

  revokeOtp(agent: Agent) {
    const body = { id: agent.id };

    this.agencyService
      .revokeOTP(body)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (response: any) => {
          const res = response.body;
          if (res.status === RESPONSE_OK) {
            this.successSave(res.data);
          }
        },
        (err) => {
          this._notificationSvc.error(
            err.error?.status,
            err.error?.error,
            err.status,
          );
        },
      );
  }

  successSave(messageKey: string, params?: any) {
    this._notificationSvc.clearNotifications();
    this._notificationSvc.success(
      this.translate.instant("NOTIFICATIONS.SUCCESS"),
      this.translate.instant(messageKey, params),
    );
  }

  cancelChanges(agent: Agent, idx: number) {
    const initialAgent = this.copiedAgents.find(
      (initialAgent) => initialAgent.id === agent.id,
    );
    if (initialAgent) {
      Object.assign(agent, initialAgent);
    }
    this.agentHasChanges[idx] = false;
  }

  parseUrl() {
    let params = this.route.snapshot.queryParams;
    this.page = parseInt(params.page) || 1;
    this.pageSize = parseInt(params.pageSize) || 15;
  }

  onPageChange() {
    let params = {};
    params["page"] = this.page;
    params["pageSize"] = this.pageSize;
    this.router.navigate([], { queryParams: params });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
